import DataTable from "@/components/application/data-table";
import Layout from "@/components/application/layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AdminPageProps, Product, Store, StoreStats } from "@/types";
import { router, usePage } from "@inertiajs/react";
import {
  CircleAlertIcon,
  MoreHorizontal,
  MoreHorizontalIcon,
  Plus,
  PuzzleIcon,
  Search,
} from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Spinner } from "@/components/ui/spinner";
import { store_product_path } from "@/lib/routes";
import { getProductState } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import EditProductSheet from "@/components/application/edit-product-sheet";
import useFilteredProducts from "@/hooks/use-filtered-products";
import NewProductSheet from "@/components/application/new-product-sheet";
import ImageWithFallback from "@/components/application/image-with-fallback";
import useDialog from "@/hooks/use-dialog";
import RegisterSaleProductDialog from "@/components/application/register-sale-product-dialog";
import RestockProductDialog from "@/components/application/restock-product-dialog";
import usePermissions from "@/hooks/use-permissions";
import { ButtonGroup } from "@/components/ui/button-group";
import PartOutSetDialog from "@/components/application/part-out-set-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import useTranslations from "@/hooks/use-translations";
import PlanLimitAlert from "@/components/application/plan-limit-alert";

function StockStats({ stats }: { stats: StoreStats }) {
  const { t } = useTranslations("admin.stores.show.stats");

  return (
    <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-2 gap-4 *:data-[slot=card]:shadow-xs @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("total_stock")}</CardDescription>
          <CardTitle className="font-semibold tabular-nums @[250px]/card:text-2xl">
            {stats.stock_count}
          </CardTitle>
        </CardHeader>
      </Card>
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("sets")}</CardDescription>
          <CardTitle className="font-semibold tabular-nums @[250px]/card:text-2xl">
            {stats.sets_count}
          </CardTitle>
        </CardHeader>
      </Card>
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("minifigures")}</CardDescription>
          <CardTitle className="font-semibold tabular-nums @[250px]/card:text-2xl">
            {stats.minifigures_count}
          </CardTitle>
        </CardHeader>
      </Card>
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("parts")}</CardDescription>
          <CardTitle className="font-semibold tabular-nums @[250px]/card:text-2xl">
            {stats.parts_count}
          </CardTitle>
        </CardHeader>
      </Card>
    </div>
  );
}

function ProductsTableRowActions({
  store,
  product,
}: {
  store: Store;
  product: Product;
}) {
  const { t } = useTranslations("admin.stores.show");
  const editProductDialog = useDialog();
  const deleteProductDialog = useDialog();
  const registerSaleProductDialog = useDialog();
  const restockProductDialog = useDialog();

  const [processing, setProcessing] = useState<boolean>(false);

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">{t("actions.open_menu")}</span>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={editProductDialog.open}>
            {t("actions.edit")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={registerSaleProductDialog.open}>
            {t("actions.register_sale")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={restockProductDialog.open}>
            {t("actions.restock")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onSelect={deleteProductDialog.open}
          >
            {t("actions.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <EditProductSheet
        product={product}
        open={editProductDialog.isOpen}
        onOpenChange={editProductDialog.setIsOpen}
      />
      <RegisterSaleProductDialog
        product={product}
        open={registerSaleProductDialog.isOpen}
        onOpenChange={registerSaleProductDialog.setIsOpen}
      />
      <RestockProductDialog
        product={product}
        open={restockProductDialog.isOpen}
        onOpenChange={restockProductDialog.setIsOpen}
      />
      <Dialog
        open={deleteProductDialog.isOpen}
        onOpenChange={deleteProductDialog.setIsOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("delete_dialog.title")}</DialogTitle>
            <DialogDescription>
              {t("delete_dialog.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              disabled={processing}
              onClick={() =>
                router.delete(store_product_path(store.id, product.id), {
                  invalidateCacheTags: ["stores", store.id ?? ""],
                  onBefore() {
                    setProcessing(true);
                  },
                  onFinish() {
                    setProcessing(false);
                  },
                })
              }
            >
              {processing && <Spinner />}
              {t("delete_dialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProductsTable({ products }: { products: Product[] }) {
  const { current_store } = usePage<AdminPageProps>().props;
  const { can } = usePermissions();
  const { t } = useTranslations("admin.stores.show");

  const newProductDialog = useDialog();
  const partOutSetDialog = useDialog();

  const columns: ColumnDef<Product>[] = useMemo(
    () => [
      {
        accessorKey: "image_url",
        header: t("table.image"),
        cell: ({ row }) => (
          <ImageWithFallback
            srcset={[
              row.original.custom_image_url,
              row.getValue("image_url"),
              `https://cdn.rebrickable.com/media/thumbs/parts/ldraw/${row.original.color_id}/${row.original.part_num}.png/250x250p.png`,
            ]}
            alt={row.getValue("name")}
            className="size-24 object-contain"
          />
        ),
      },
      {
        id: "name",
        header: t("table.name"),
        meta: {
          className: "w-1/3",
        },
        cell({ row }) {
          return (
            <>
              <p className="text-sm font-medium whitespace-break-spaces">
                {row.original.name}
              </p>
              <p className="text-sm">{row.original.system_code}</p>
            </>
          );
        },
      },
      {
        accessorKey: "product_type",
        header: t("table.type"),
        cell: ({ row }) => (
          <span className="capitalize">{row.getValue("product_type")}</span>
        ),
      },
      {
        accessorKey: "state",
        header: t("table.state"),
        cell: ({ row }) => getProductState(row.original, t),
      },
      {
        accessorKey: "added_by",
        header: t("table.added_by"),
      },
      {
        accessorKey: "price_cents",
        header: t("table.price"),
        cell: ({ row }) =>
          new Intl.NumberFormat(navigator.language, {
            style: "currency",
            currency: current_store?.currency.toUpperCase() || "USD",
          }).format((row.getValue("price_cents") as number) / 100),
      },
      {
        accessorKey: "stock",
        header: t("table.stock"),
      },
      {
        id: "actions",
        cell: ({ row }) => {
          const product = row.original;

          return (
            <ProductsTableRowActions
              key={product.id}
              store={current_store!}
              product={product}
            />
          );
        },
      },
    ],
    [current_store?.currency]
  );

  const { filter, setFilter, search, setSearch, filteredProducts } =
    useFilteredProducts(products);

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col-reverse md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col-reverse md:flex-row items-center justify-between gap-4 w-full md:w-auto">
            <ToggleGroup
              type="single"
              variant="outline"
              value={filter.product_type}
              onValueChange={(value) => setFilter({ product_type: value })}
            >
              <ToggleGroupItem value="all" aria-label={t("filters.show_all")}>
                {t("filters.all")}
              </ToggleGroupItem>
              <ToggleGroupItem value="set" aria-label={t("filters.show_sets")}>
                {t("filters.sets")}
              </ToggleGroupItem>
              <ToggleGroupItem
                value="minifigure"
                aria-label={t("filters.show_minifigures")}
              >
                {t("filters.minifigures")}
              </ToggleGroupItem>
              <ToggleGroupItem
                value="part"
                aria-label={t("filters.show_parts")}
              >
                {t("filters.parts")}
              </ToggleGroupItem>
            </ToggleGroup>
            <InputGroup>
              <InputGroupInput
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("search_placeholder")}
              />
              <InputGroupAddon>
                <Search />
              </InputGroupAddon>
            </InputGroup>
          </div>
          <ButtonGroup>
            <Button
              variant="outline"
              disabled={!can("create_product")}
              onClick={newProductDialog.open}
            >
              <Plus />
              {t("new_product")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t("more_options")}
                >
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    disabled={!can("create_product")}
                    onClick={partOutSetDialog.open}
                  >
                    <PuzzleIcon /> {t("part_out_set")}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </ButtonGroup>
        </div>

        <DataTable<Product> columns={columns} data={filteredProducts} />
      </div>
      <NewProductSheet
        open={newProductDialog.isOpen}
        onOpenChange={newProductDialog.setIsOpen}
      />
      <PartOutSetDialog
        open={partOutSetDialog.isOpen}
        onOpenChange={partOutSetDialog.setIsOpen}
      />
    </>
  );
}
function StorePage({
  stats,
  products,
}: {
  stats: StoreStats;
  products: Product[];
}) {
  const { can } = usePermissions();
  const { t } = useTranslations();

  return (
    <>
      {!can("create_product") && (
        <PlanLimitAlert title={t("shared.plan_limit_alert.products.title")} />
      )}
      <StockStats stats={stats} />
      <ProductsTable products={products} />
    </>
  );
}

StorePage.layout = (page: ReactNode) => <Layout children={page} />;

export default StorePage;
